import { Transform } from "./transform";

/** A tree of transforms to use as scene graph */
export class SceneGraphNode extends eXpress.tree.Node<SceneGraphNode> {
	public readonly name: string;
	private _transform: Transform;

	constructor(
		/** Only for debug purpose */
		name: string,
		/** Pass null for a root node */
		parent: SceneGraphNode | null,
		transform = Transform.unit,
	) {
		super(parent || null);
		this.name = name;
		this._transform = transform;
	}

	protected cloneSelf(newParent: SceneGraphNode | null = null) {
		return new SceneGraphNode(this.name, newParent, this.transform);
	}

	public override toString(type: "node" | "with-descendents" | "with-ancestors" = "node") {
		const formatNode = (node: SceneGraphNode) =>
			`${node.name}: ${node.transform.toString()} (#${node.oid})`;

		if (type === "node") return formatNode(this);

		// Indent each child
		let text = "";
		if (type === "with-ancestors") {
			text = [this, ...this.ancestors()]
				.reverse()
				.map((node, index) => `${"".padStart(index * 2)}${formatNode(node)}`)
				.join("\n");
		} else {
			this.selfDescend((node: any, currentIndent) => {
				text += `${currentIndent}${formatNode(node)}\n`;
				return `${currentIndent}  `;
			}, "");
		}
		return text;
	}

	public get transform() {
		return this._transform;
	}

	public setTransform(transform: Transform) {
		// We can later see if we also want to fire events
		this._transform = transform;
	}

	/**
	 * Returns the combined transform from this node to the ancestor node, upto but excluding the ancestor node.
	 * NOTE: throws an exception if not found
	 */
	private static getTransformToAncestor(
		child: SceneGraphNode,
		ancestor: SceneGraphNode | null,
		throwIfNotFound?: true,
	): Transform;

	private static getTransformToAncestor(
		child: SceneGraphNode,
		ancestor: SceneGraphNode | null,
		throwIfNotFound: boolean,
	): Transform | null;

	private static getTransformToAncestor(
		child: SceneGraphNode,
		ancestor: SceneGraphNode | null,
		throwIfNotFound = true,
	) {
		// Loop
		let node: SceneGraphNode | null = child;
		let transform = Transform.unit;
		while (node && node !== ancestor) {
			transform = transform.mul(node.transform);
			node = node.parent;
		}

		if (node === ancestor) return transform;

		if (throwIfNotFound) {
			throw new eXpress.GeneralException(
				"TransformSceneNodeException",
				`Node '${child.toString()}' doesn't has '${ancestor?.toString()}' as ancestor.`,
			);
		}

		return null;
	}

	/**
	 * Returns the combined transform from this node to a child node, upto but excluding the child node.
	 * NOTE: throws an exception if not found
	 */
	private static getTransformToChild(
		ancestor: SceneGraphNode | null,
		child: SceneGraphNode,
		throwIfNotFound?: true,
	): Transform;

	private static getTransformToChild(
		ancestor: SceneGraphNode | null,
		child: SceneGraphNode,
		throwIfNotFound: boolean,
	): Transform | null;

	private static getTransformToChild(
		ancestor: SceneGraphNode | null,
		child: SceneGraphNode,
		throwIfNotFound = true,
	) {
		if (!child) return null;
		const transform = this.getTransformToAncestor(child, ancestor, false);

		if (transform) return transform.inverse();

		if (throwIfNotFound) {
			throw new eXpress.GeneralException(
				"TransformSceneNodeException",
				`Node '${ancestor?.toString()}' doesn't has '${child.toString()}' as child.`,
			);
		}

		return null;
	}

	/**
	 * Returns the combined transform from one node to another node in the same graph
	 * Added a static method so we can got from the root (null) to a node
	 */

	public static getTransformTo(
		fromNode: SceneGraphNode | null,
		toNode: SceneGraphNode | null,
		throwIfNotFound?: true,
	): Transform;

	public static getTransformTo(
		fromNode: SceneGraphNode | null,
		toNode: SceneGraphNode | null,
		throwIfNotFound: boolean,
	): Transform | null;

	public static getTransformTo(
		fromNode: SceneGraphNode | null,
		toNode: SceneGraphNode | null,
		throwIfNotFound = true,
	) {
		if (!fromNode && !toNode) return Transform.unit;
		if (fromNode && !toNode)
			return this.getTransformToAncestor(fromNode, null, throwIfNotFound);
		if (!fromNode && toNode) return this.getTransformToChild(null, toNode, throwIfNotFound);
		if (fromNode && toNode) {
			// TODO: Optimize this code if source is an ancestor of target, or vice versa?
			const sourcePathToRoot = fromNode.getPathToRoot();
			const targetPathToRoot = toNode.getPathToRoot();

			const commonAncestor = sourcePathToRoot.getCommonAncestor(targetPathToRoot.toMap());
			if (!commonAncestor) {
				if (throwIfNotFound) {
					throw new eXpress.GeneralException(
						"TransformSceneNodeException",
						`No common ancestor found between '${fromNode.name}' and '${
							toNode.name
						}'.\n\n${fromNode.toString("with-ancestors")}\n${toNode.toString(
							"with-ancestors",
						)}`,
					);
				}
				return null;
			}

			const sourceToAncestorTransform = eXpress.utils.assert(
				this.getTransformToAncestor(fromNode, commonAncestor, false),
			);
			const targetToAncestorTransform = eXpress.utils.assert(
				this.getTransformToAncestor(toNode, commonAncestor, false),
			);
			return sourceToAncestorTransform.mul(targetToAncestorTransform.inverse());
		}

		// We should never reach this code, needed for eslint rule consistent-return
		return Transform.unit;
	}

	/**
	 * Returns the combined transform from this node to another node in the graph
	 */
	public getTransformTo(targetNode: SceneGraphNode | null, throwIfNotFound?: true): Transform;

	public getTransformTo(
		targetNode: SceneGraphNode | null,
		throwIfNotFound: boolean,
	): Transform | null;

	public getTransformTo(targetNode: SceneGraphNode | null, throwIfNotFound = true) {
		return SceneGraphNode.getTransformTo(this, targetNode, throwIfNotFound);
	}
}
